import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';

import {
  downloadPublicInvoicePdf,
  retrievePublicInvoice,
  retrievePublicPaymentIntent,
} from '@src/api/invoices';
import houseLogo from '@src/assets/logo-full.png';

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

const sanitizeLine = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  const stringValue = String(value).trim();
  return stringValue.length ? stringValue : null;
};

const dedupeLines = (lines) => {
  const seen = new Set();
  return lines.filter((line) => {
    const sanitized = sanitizeLine(line);
    if (!sanitized) {
      return false;
    }
    if (seen.has(sanitized)) {
      return false;
    }
    seen.add(sanitized);
    return true;
  });
};

const buildAddressLines = (address) => {
  if (!address || typeof address !== 'object') {
    return [];
  }
  if (Array.isArray(address.lines)) {
    return dedupeLines(address.lines.map(sanitizeLine));
  }
  const line1 = sanitizeLine(address.line1 || address.address1 || address.address_line1);
  const line2 = sanitizeLine(address.line2 || address.address2 || address.address_line2);
  const city = sanitizeLine(address.city);
  const state = sanitizeLine(address.state || address.region);
  const postal = sanitizeLine(address.postal_code || address.postalCode || address.zip);
  const country = sanitizeLine(address.country);
  const lines = [line1, line2];
  const cityLineParts = [city, state, postal].filter(Boolean);
  if (cityLineParts.length) {
    lines.push(cityLineParts.join(', '));
  }
  lines.push(country);
  return dedupeLines(lines);
};

const buildCompanyLines = (snapshot, defaults = []) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return defaults;
  }
  if (Array.isArray(snapshot.lines)) {
    const prepared = dedupeLines(snapshot.lines.map(sanitizeLine));
    if (prepared.length) {
      return prepared;
    }
  }
  const name = sanitizeLine(snapshot.name || snapshot.company || snapshot.company_name);
  const addressLines = [
    ...buildAddressLines(snapshot),
    ...buildAddressLines(snapshot.address),
  ];
  const extras = [
    sanitizeLine(snapshot.email),
    sanitizeLine(snapshot.phone),
    sanitizeLine(snapshot.website || snapshot.url),
  ];
  const combined = dedupeLines([name, ...addressLines, ...extras]);
  return combined.length ? combined : defaults;
};

const DEFAULT_FROM_LINES = [
  'Lead Gurus',
  '1225 Ave. Ponce de León',
  'PH-881',
  'San Juan, PR 00907',
  'United States of America',
  'billing@leadgurus.com',
];

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return null;
  }
  return numberValue;
};

const normalizePaymentOptions = (invoice) => {
  const snapshot =
    invoice?.payment_options_snapshot ||
    invoice?.processing_fee_snapshot ||
    invoice?.stripe_payment_options_snapshot ||
    invoice?.payment_option_snapshot;

  if (!snapshot) {
    return [];
  }

  const entries = Array.isArray(snapshot)
    ? snapshot.map((entry) => [entry?.type || entry?.payment_method, entry])
    : Object.entries(snapshot);

  return entries
    .map(([key, raw]) => {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const type = raw.type || raw.payment_method || raw.paymentMethod || key;
      if (!type) {
        return null;
      }

      const totalRaw =
        raw.total_with_fee ||
        raw.total_amount ||
        raw.amount_with_fee ||
        raw.total ||
        raw.totalDue ||
        raw.amount;
      const feeAmountRaw =
        raw.processing_fee_amount ||
        raw.processing_fee ||
        raw.fee_amount ||
        raw.fee ||
        raw.total_fee;
      const feePercentRaw =
        raw.processing_fee_percent ||
        raw.fee_percent ||
        raw.percent_fee ||
        raw.percent;
      const feeFlatRaw =
        raw.processing_fee_flat_amount ||
        raw.processing_fee_flat ||
        raw.fee_flat_amount ||
        raw.fee_flat ||
        raw.flat_fee;

      return {
        type,
        label: raw.label || raw.name || raw.display_name || raw.displayName || type,
        totalWithFee: parseAmount(totalRaw),
        totalWithFeeRaw: totalRaw,
        feeAmount: parseAmount(feeAmountRaw),
        feeAmountRaw,
        feePercent: parseAmount(feePercentRaw),
        feePercentRaw,
        feeFlat: parseAmount(feeFlatRaw),
        feeFlatRaw,
      };
    })
    .filter(Boolean);
};

const getPaymentActionGuidance = (result) => {
  const status = result?.paymentIntent?.status || '';
  const nextActionType = result?.paymentIntent?.next_action?.type || '';
  const errorCode = String(result?.error?.code || '').toLowerCase();
  const errorMessage = String(result?.error?.message || '').toLowerCase();
  const hasMicrodepositHint =
    errorCode.includes('microdeposit') || errorMessage.includes('microdeposit');

  if (status === 'requires_action') {
    if (nextActionType === 'verify_with_microdeposits' || hasMicrodepositHint) {
      return (
        'Additional bank verification is required. Stripe sent two microdeposits to the bank ' +
        'account. Verify those amounts in Stripe to complete payment.'
      );
    }
    if (nextActionType === 'redirect_to_url' || nextActionType === 'use_stripe_sdk') {
      return (
        'Additional bank authentication is required. Follow the Stripe prompt to finish ' +
        'verification and submit payment.'
      );
    }
    return (
      'Additional verification is required by your bank. Follow the Stripe prompt to complete ' +
      'payment, then retry if needed.'
    );
  }

  if (hasMicrodepositHint) {
    return (
      'Bank verification via microdeposits is required before this ACH payment can complete. ' +
      'Please verify the deposited amounts in Stripe and submit payment again.'
    );
  }

  return null;
};

const getUnexpectedPaymentErrorMessage = (error) => {
  const reason = String(error?.message || '').trim();
  const code = String(error?.code || '').trim();
  if (reason && code) {
    return `Payment could not be confirmed: ${reason} (${code}).`;
  }
  if (reason) {
    return `Payment could not be confirmed: ${reason}`;
  }
  if (code) {
    return `Payment could not be confirmed (${code}).`;
  }
  return 'Unexpected error confirming payment.';
};

const getPersistedPaymentFailureMessage = (invoice) => {
  if (!invoice || typeof invoice !== 'object') {
    return '';
  }
  const rawMessage = String(invoice.payment_failure_message || '').trim();
  if (rawMessage) {
    return rawMessage;
  }
  if (invoice.payment_failed_at) {
    return 'A previous bank payment attempt failed. Please verify account details and try again.';
  }
  return '';
};

const MANUAL_ACH_HELPER_TEXT =
  'If you enter bank details manually, your bank may require microdeposit verification (1-2 business days). ' +
  'Bank login is usually faster.';

function PaymentSummary({ baseAmount, baseAmountRaw, selectedOption, options, onOptionSelect }) {
  const displayBase = formatCurrency(baseAmountRaw ?? baseAmount);
  const totalAmount = selectedOption?.totalWithFee ?? baseAmount;
  const displayTotal = formatCurrency(
    selectedOption?.totalWithFeeRaw ?? totalAmount ?? baseAmountRaw ?? baseAmount,
  );
  const feeAmount =
    selectedOption?.feeAmount != null
      ? formatCurrency(selectedOption.feeAmount)
      : selectedOption?.feeAmountRaw
        ? formatCurrency(selectedOption.feeAmountRaw)
        : null;

  const percentLabel = (() => {
    if (!selectedOption?.feePercentRaw && selectedOption?.feePercent == null) {
      return '';
    }
    if (selectedOption?.feePercentRaw) {
      return `${selectedOption.feePercentRaw}%`;
    }
    const percent = selectedOption?.feePercent;
    if (percent == null) {
      return '';
    }
    return `${percent.toFixed(2).replace(/\.00$/, '')}%`;
  })();

  const flatFeeLabel = (() => {
    if (!selectedOption) {
      return '';
    }
    if (selectedOption.feeFlatRaw) {
      return formatCurrency(selectedOption.feeFlatRaw);
    }
    if (selectedOption.feeFlat != null) {
      return formatCurrency(selectedOption.feeFlat);
    }
    return '';
  })();

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Invoice total
        </Typography>
        <Typography variant="body1">{displayBase}</Typography>
      </Box>
      {options.length ? (
        <Stack spacing={1}>
          {options.map((option) => {
            const optionTotal = option.totalWithFee ?? baseAmount;
            const optionDisplayTotal = formatCurrency(
              option.totalWithFeeRaw ?? optionTotal ?? baseAmountRaw ?? baseAmount,
            );
            const isSelected = option.type === selectedOption?.type;
            const optionFee =
              option.feeAmount != null
                ? formatCurrency(option.feeAmount)
                : option.feeAmountRaw
                  ? formatCurrency(option.feeAmountRaw)
                  : '—';
            const optionPercentLabel = (() => {
              if (option.feePercentRaw) {
                return `${option.feePercentRaw}%`;
              }
              if (option.feePercent == null) {
                return '';
              }
              return `${option.feePercent.toFixed(2).replace(/\.00$/, '')}%`;
            })();
            const optionFlatLabel = (() => {
              if (option.feeFlatRaw) {
                return formatCurrency(option.feeFlatRaw);
              }
              if (option.feeFlat != null) {
                return formatCurrency(option.feeFlat);
              }
              return '';
            })();

            return (
              <Box
                key={option.type}
                data-testid={`payment-option-${option.type}`}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.hover' : 'transparent',
                  px: 2,
                  py: 1.5,
                  cursor: onOptionSelect ? 'pointer' : 'default',
                }}
                onClick={onOptionSelect ? () => onOptionSelect(option.type) : undefined}
                role={onOptionSelect ? 'button' : undefined}
                tabIndex={onOptionSelect ? 0 : undefined}
                onKeyDown={
                  onOptionSelect
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onOptionSelect(option.type);
                        }
                      }
                    : undefined
                }
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {option.label}{' '}
                    {isSelected ? (
                      <Typography component="span" variant="caption" color="primary">
                        (Selected)
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Processing fee: {optionFee}
                    {optionPercentLabel ? ` (${optionPercentLabel}${optionFlatLabel ? ` + ${optionFlatLabel}` : ''})` : ''}
                    {!optionPercentLabel && optionFlatLabel ? ` (${optionFlatLabel})` : ''}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Total: {optionDisplayTotal}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Amount to be charged
        </Typography>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
          {displayTotal}
        </Typography>
        {feeAmount && (percentLabel || flatFeeLabel) ? (
          <Typography variant="caption" color="text.secondary">
            Includes processing fee {feeAmount}
            {percentLabel ? ` (${percentLabel}${flatFeeLabel ? ` + ${flatFeeLabel}` : ''})` : ''}
            {!percentLabel && flatFeeLabel ? ` (${flatFeeLabel})` : ''}
          </Typography>
        ) : feeAmount ? (
          <Typography variant="caption" color="text.secondary">
            Includes processing fee {feeAmount}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function PaymentFormContent({
  invoiceTotal,
  invoiceTotalRaw,
  options,
  selectedOption,
  onSelectionChange,
  currentSelection,
  selectionDisabled,
  onSuccess,
  onError,
  submitting,
  setSubmitting,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementComplete, setElementComplete] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    if (!elementReady || !elements.getElement(PaymentElement)) {
      onError('Payment form is still loading. Please wait a moment and try again.');
      return;
    }
    onError('');
    onSuccess('');
    setSubmitting(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        const guidance = getPaymentActionGuidance(result);
        onError(guidance || result.error.message || 'Payment could not be completed.');
        setSubmitting(false);
        return;
      }

      const status = result.paymentIntent?.status;
      if (status && status !== 'succeeded' && status !== 'processing') {
        const guidance = getPaymentActionGuidance(result);
        onError(guidance || `Payment status: ${status}`);
        setSubmitting(false);
        return;
      }

      onSuccess(
        status === 'processing'
          ? 'Payment submitted. Stripe is still processing your payment.'
          : "Payment confirmed! We'll email you a receipt shortly.",
        status || '',
      );
    } catch (err) {
      console.error('Unexpected payment error', err);
      onError(getUnexpectedPaymentErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleElementChange = (event) => {
    if (typeof event?.complete === 'boolean') {
      setElementComplete(event.complete);
    }
    const methodType = event?.value?.type;
    if (methodType && !selectionDisabled && methodType !== currentSelection) {
      onSelectionChange(methodType);
    }
  };

  const order = selectedOption?.type
    ? [selectedOption.type]
    : options.length
      ? options.map((option) => option.type)
      : undefined;
  const totalForButton =
    selectedOption?.totalWithFeeRaw ??
    selectedOption?.totalWithFee ??
    invoiceTotal ??
    invoiceTotalRaw;
  const buttonLabel = `Pay ${formatCurrency(totalForButton)} securely`;

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <PaymentElement
          onReady={() => setElementReady(true)}
          onChange={handleElementChange}
        />
        {currentSelection === 'us_bank_account' ? (
          <Typography variant="caption" color="text.secondary">
            {MANUAL_ACH_HELPER_TEXT}
          </Typography>
        ) : null}
        <PaymentSummary
          baseAmount={invoiceTotal}
          baseAmountRaw={invoiceTotalRaw}
          selectedOption={selectedOption}
          options={options}
          onOptionSelect={onSelectionChange}
        />
        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!stripe || !elements || submitting || !elementComplete || !elementReady}
            sx={{ borderRadius: 9999 }}
          >
            {submitting ? 'Processing…' : buttonLabel}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

function PaymentForm({
  clientSecret,
  stripePromise,
  invoiceTotal,
  invoiceTotalRaw,
  options,
  selectedOption,
  onSelectionChange,
  selectionDisabled,
  onSuccess,
  onError,
  submitting,
  setSubmitting,
}) {
  if (!clientSecret || !stripePromise) {
    return null;
  }

  const order = options.length ? options.map((option) => option.type) : undefined;

  return (
    <Elements
      key={`${clientSecret}-${selectedOption?.type || 'default'}`}
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          labels: 'floating',
        },
        ...(order ? { paymentMethodOrder: order } : {}),
      }}
    >
      <PaymentFormContent
        invoiceTotal={invoiceTotal}
        invoiceTotalRaw={invoiceTotalRaw}
        options={options}
        selectedOption={selectedOption}
        onSelectionChange={onSelectionChange}
        currentSelection={selectedOption?.type}
        selectionDisabled={selectionDisabled}
        onSuccess={onSuccess}
        onError={onError}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />
    </Elements>
  );
}

export default function PublicInvoicePage() {
  const { number } = useParams();
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token');
  const token = useMemo(
    () => (rawToken ? rawToken.replace(/\s/g, '+') : rawToken),
    [rawToken],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentSubmissionStatus, setPaymentSubmissionStatus] = useState('');
  const [paymentIntentRefreshNonce, setPaymentIntentRefreshNonce] = useState(0);
  const [clientSecret, setClientSecret] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [achDialogOpen, setAchDialogOpen] = useState(false);
  const paymentIntentRequestRef = useRef(0);

  const isPaid = useMemo(
    () => (invoice?.status ? String(invoice.status).toLowerCase() === 'paid' : false),
    [invoice],
  );
  const paymentComplete = isPaid || paymentSubmissionStatus === 'succeeded';
  const persistedPaymentFailureMessage = useMemo(
    () => getPersistedPaymentFailureMessage(invoice),
    [invoice],
  );

  useEffect(() => {
    if (!number) {
      setError('Missing invoice number.');
      setLoading(false);
      return;
    }
    if (!rawToken) {
      setError('Access token required to view this invoice.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setDownloadError('');
    setDownloadLoading(false);
    setPaymentIntentError('');
    setPaymentSuccess('');
    setPaymentError('');
    setPaymentSubmissionStatus('');
    setClientSecret('');
    setSelectedPaymentMethod('');
    retrievePublicInvoice({ number, token })
      .then((res) => {
        setInvoice(res.data);
      })
      .catch((err) => {
        console.error('Unable to load public invoice', err);
        const detail = err?.response?.data?.detail;
        setError(detail || 'We could not verify this invoice link.');
      })
      .finally(() => setLoading(false));
  }, [number, rawToken, token]);

  useEffect(() => {
    if (
      !invoice ||
      !number ||
      !token ||
      !selectedPaymentMethod ||
      isPaid ||
      paymentComplete ||
      paymentSubmissionStatus === 'processing'
    ) {
      return;
    }
    paymentIntentRequestRef.current += 1;
    const requestId = paymentIntentRequestRef.current;
    setClientSecret('');
    setPaymentIntentLoading(true);
    setPaymentIntentError('');
    const timer = setTimeout(() => {
      retrievePublicPaymentIntent({ number, token, payment_method: selectedPaymentMethod })
        .then((res) => {
          if (requestId !== paymentIntentRequestRef.current) {
            return;
          }
          const data = res.data || {};
          if (data.publishable_key) {
            setStripePromise((prev) => prev || loadStripe(data.publishable_key));
          }
          if (data.client_secret) {
            setClientSecret(data.client_secret);
          }
        })
        .catch((err) => {
          console.error('Unable to initialize payment intent', err);
          const detail = err?.response?.data?.detail;
          setPaymentIntentError(detail || 'We could not initialize the payment form.');
        })
        .finally(() => setPaymentIntentLoading(false));
    }, 150);
    return () => clearTimeout(timer);
  }, [
    invoice?.number,
    number,
    token,
    selectedPaymentMethod,
    isPaid,
    paymentComplete,
    paymentSubmissionStatus,
    paymentIntentRefreshNonce,
  ]);

  useEffect(() => {
    if (!invoice || !number || !token || isPaid || paymentSubmissionStatus !== 'processing') {
      return undefined;
    }
    const interval = setInterval(() => {
      retrievePublicInvoice({ number, token })
        .then((res) => {
          const latestInvoice = res.data || null;
          setInvoice(latestInvoice);
          const hasFailure =
            latestInvoice &&
            (String(latestInvoice.payment_failure_message || '').trim() ||
              latestInvoice.payment_failed_at);
          if (hasFailure) {
            setPaymentSubmissionStatus('');
            setPaymentSuccess('');
          }
          if (latestInvoice?.status && String(latestInvoice.status).toLowerCase() === 'paid') {
            setPaymentSubmissionStatus('succeeded');
            setPaymentSuccess("Payment confirmed! We'll email you a receipt shortly.");
          }
        })
        .catch((err) => {
          console.error('Unable to refresh invoice payment status', err);
        });
    }, 8000);
    return () => clearInterval(interval);
  }, [invoice, isPaid, number, paymentSubmissionStatus, token]);

  const handleDownloadPdf = () => {
    if (!invoice) {
      return;
    }
    setDownloadLoading(true);
    setDownloadError('');
    downloadPublicInvoicePdf({ number, token })
      .then((blob) => {
        const fileName = `${invoice.number || number}.pdf`;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        console.error('Unable to download public invoice PDF', err);
        const detail = err?.response?.data?.detail;
        setDownloadError(detail || 'We could not download the invoice PDF.');
      })
      .finally(() => setDownloadLoading(false));
  };

  const handleDownloadTerritoryCsv = () => {
    if (!invoice?.territory_breakdown?.length) {
      return;
    }
    const safeValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      const stringValue = String(value);
      if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };
    const formatCsvDate = (value) => {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().slice(0, 10);
    };
    const periodStart = formatCsvDate(invoice.period_start);
    const periodEnd = formatCsvDate(invoice.period_end);
    const rows = [
      ['Territory', 'Vertical', 'Period start', 'Period end', 'Spend'],
      ...invoice.territory_breakdown.map((row) => [
        safeValue(row.territory || ''),
        safeValue(row.vertical || 'Unassigned'),
        safeValue(periodStart),
        safeValue(periodEnd),
        safeValue(row.spend || '0.00'),
      ]),
    ];
    if (invoice.territory_breakdown_total) {
      rows.push([
        'Total',
        '',
        safeValue(periodStart),
        safeValue(periodEnd),
        safeValue(invoice.territory_breakdown_total),
      ]);
    }
    const csvContent = rows.map((row) => row.map(safeValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `${invoice.number || number}-territory-vertical-breakdown.csv`;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadClientBreakdownCsv = () => {
    if (!clientBreakdown.length || !invoice) {
      return;
    }
    const safeValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      const stringValue = String(value);
      if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };
    const formatCsvDate = (value) => {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().slice(0, 10);
    };
    const periodStart = formatCsvDate(invoice.period_start);
    const periodEnd = formatCsvDate(invoice.period_end);
    const rows = [
      ['Client', 'Period start', 'Period end', 'Spend', 'Fee', 'Line total'],
      ...clientBreakdown.map((row) => [
        safeValue(row.client_name || ''),
        safeValue(periodStart),
        safeValue(periodEnd),
        safeValue(row.spend_amount || '0.00'),
        safeValue(row.fee_amount || '0.00'),
        safeValue(row.line_total || '0.00'),
      ]),
    ];
    rows.push([
      'Total',
      safeValue(periodStart),
      safeValue(periodEnd),
      '',
      '',
      safeValue(invoice.total_amount || '0.00'),
    ]);
    const csvContent = rows.map((row) => row.map(safeValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `${invoice.number || number}-client-breakdown.csv`;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const achLines = useMemo(() => {
    if (!invoice?.ach_instructions) {
      return [];
    }
    return invoice.ach_instructions.split('\n').filter((line) => line.trim());
  }, [invoice]);

  const pageTitle = invoice
    ? `Invoice ${invoice.number} · Lead Gurus`
    : 'Invoice · Lead Gurus';

  const fromLines = useMemo(
    () => buildCompanyLines(invoice?.billing_from_snapshot, DEFAULT_FROM_LINES),
    [invoice],
  );

  const paymentOptions = useMemo(() => normalizePaymentOptions(invoice), [invoice]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      return;
    }
    if (paymentOptions.length) {
      setSelectedPaymentMethod(paymentOptions[0].type);
      return;
    }
    setSelectedPaymentMethod('card');
  }, [paymentOptions, selectedPaymentMethod]);

  useEffect(() => {
    if (!isPaid) {
      return;
    }
    setPaymentIntentError('');
    setPaymentError('');
    setPaymentSuccess("Payment received. We'll email you a receipt shortly.");
    setClientSecret('');
  }, [isPaid]);

  const invoiceTotalAmount = useMemo(() => parseAmount(invoice?.total_amount), [invoice]);
  const clientBreakdown = useMemo(() => {
    if (!invoice) {
      return [];
    }
    if (Array.isArray(invoice.client_breakdown)) {
      return invoice.client_breakdown;
    }
    if (Array.isArray(invoice.client_breakdown_snapshot)) {
      return invoice.client_breakdown_snapshot;
    }
    return [];
  }, [invoice]);
  const selectedOption = useMemo(
    () => paymentOptions.find((option) => option.type === selectedPaymentMethod) || paymentOptions[0],
    [paymentOptions, selectedPaymentMethod],
  );

  const billToLines = useMemo(() => {
    if (!invoice) {
      return [];
    }
    const lines = [];
    const clientName = sanitizeLine(invoice.client_name);
    if (clientName) {
      lines.push(clientName);
    }
    if (Array.isArray(invoice.billing_contacts_snapshot)) {
      invoice.billing_contacts_snapshot.forEach((contact) => {
        if (!contact || typeof contact !== 'object') {
          return;
        }
        const name = sanitizeLine(contact.name);
        const email = sanitizeLine(contact.email || contact.address);
        const phone = sanitizeLine(contact.phone);
        const parts = [name, email].filter(Boolean);
        if (parts.length) {
          lines.push(parts.join(' • '));
        } else if (phone) {
          lines.push(phone);
        }
      });
    }
    buildAddressLines(invoice.billing_address_snapshot).forEach((line) => {
      lines.push(line);
    });
    return dedupeLines(lines);
  }, [invoice]);

  const attachments = invoice?.attachments ?? [];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Stack spacing={4}>
        <Box textAlign="center">
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
            Lead Gurus Billing
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>
            Invoice Summary
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {invoice
              ? `${invoice.client_name || 'Client'} • ${invoice.period_label || 'Billing period'}`
              : 'Review invoice details and pay securely'}
          </Typography>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : invoice ? (
          <Stack spacing={4}>
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <CardHeader
                sx={{ pb: 0 }}
                title={
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          component="img"
                          src={houseLogo}
                          alt="Lead Gurus logo"
                          sx={{ height: 40, width: 'auto' }}
                        />
                        {invoice.client_logo_file ? (
                          <Box
                            component="img"
                            src={invoice.client_logo_file}
                            alt={`${invoice.client_name || 'Client'} logo`}
                            sx={{
                              height: 40,
                              width: 'auto',
                              maxWidth: { xs: '100%', sm: 160 },
                              objectFit: 'contain',
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Box textAlign={{ xs: 'left', sm: 'right' }}>
                        <Stack spacing={1} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Invoice {invoice.number}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {invoice.period_label}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleDownloadPdf}
                          disabled={downloadLoading}
                          sx={{ borderRadius: 9999, textTransform: 'none', mt: 1 }}
                        >
                          {downloadLoading ? 'Downloading…' : 'Download PDF'}
                        </Button>
                      </Box>
                    </Stack>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={4}>
                  {downloadError ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {downloadError}
                    </Alert>
                  ) : null}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        From
                      </Typography>
                      {fromLines.length ? (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          {fromLines.map((line, index) => (
                            <Typography
                              key={`from-${index}`}
                              variant={index === 0 ? 'body1' : 'body2'}
                              color={index === 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          Company details unavailable.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Bill to
                      </Typography>
                      {billToLines.length ? (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          {billToLines.map((line, index) => (
                            <Typography
                              key={`bill-to-${index}`}
                              variant={index === 0 ? 'body1' : 'body2'}
                              color={index === 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          Billing contact information unavailable.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Client
                      </Typography>
                      <Typography variant="h6">{invoice.client_name}</Typography>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }}>
                        Amount due
                      </Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                        {formatCurrency(invoice.total_amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Issue date
                          </Typography>
                          <Typography>{formatDate(invoice.issue_date)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Due date
                          </Typography>
                          <Typography>{formatDate(invoice.due_date)}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
                {invoice.external_notes ? (
                  <Alert severity="info" sx={{ mt: 4, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Client message
                    </Typography>
                    <Typography whiteSpace="pre-line">{invoice.external_notes}</Typography>
                  </Alert>
                ) : null}
              </CardContent>
            </Card>

            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <CardHeader title="Pay securely online" sx={{ pb: 0 }} />
              <CardContent>
                <Stack spacing={3}>
                  {paymentIntentError && !paymentComplete ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {paymentIntentError}
                    </Alert>
                  ) : null}
                  {paymentSubmissionStatus === 'processing' &&
                  !isPaid &&
                  !paymentError &&
                  !paymentIntentError &&
                  !persistedPaymentFailureMessage ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {paymentSuccess ||
                        'Payment submitted. Stripe is processing your payment. We will update this page automatically when a final result is available.'}
                    </Alert>
                  ) : null}
                  {paymentComplete ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      {paymentSuccess || "Payment received. We'll email you a receipt shortly."}
                    </Alert>
                  ) : null}
                  {paymentError && !paymentComplete ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {paymentError}
                    </Alert>
                  ) : null}
                  {persistedPaymentFailureMessage &&
                  !paymentComplete &&
                  !paymentError &&
                  !paymentSubmitting ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {persistedPaymentFailureMessage}
                    </Alert>
                  ) : null}
                  {selectedPaymentMethod === 'us_bank_account' &&
                  !paymentComplete &&
                  !paymentError &&
                  !paymentIntentError &&
                  !persistedPaymentFailureMessage &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Manual bank entry may require microdeposit verification (1-2 business days). Bank login is usually faster.
                    </Alert>
                  ) : null}
                  {(paymentIntentError || paymentError || persistedPaymentFailureMessage) &&
                  !paymentComplete &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setPaymentIntentError('');
                          setPaymentError('');
                          setPaymentSuccess('');
                          setPaymentSubmissionStatus('');
                          setPaymentIntentRefreshNonce((prev) => prev + 1);
                        }}
                      >
                        Try again
                      </Button>
                    </Box>
                  ) : null}
                  {paymentIntentLoading && !clientSecret && !paymentComplete ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
                      <CircularProgress color="primary" size={32} />
                    </Box>
                  ) : null}
                  {clientSecret &&
                  stripePromise &&
                  !paymentComplete &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <PaymentForm
                      clientSecret={clientSecret}
                      stripePromise={stripePromise}
                      invoiceTotal={invoiceTotalAmount}
                      invoiceTotalRaw={invoice?.total_amount}
                      options={paymentOptions}
                      selectedOption={selectedOption}
                      onSelectionChange={(type) => setSelectedPaymentMethod(type)}
                      selectionDisabled={paymentIntentLoading}
                      onSuccess={(message, status) => {
                        if (!message) {
                          setPaymentSubmissionStatus('');
                          setPaymentSuccess('');
                          return;
                        }
                        if (status === 'processing') {
                          setPaymentSubmissionStatus('processing');
                        } else {
                          setPaymentSubmissionStatus('succeeded');
                        }
                        setPaymentSuccess(message);
                        setPaymentIntentError('');
                        setPaymentError('');
                      }}
                      onError={(message) => {
                        setPaymentSubmissionStatus('');
                        setPaymentError(message);
                        if (message) {
                          setPaymentSuccess('');
                        }
                      }}
                      submitting={paymentSubmitting}
                      setSubmitting={setPaymentSubmitting}
                    />
                  ) : null}
                  {!clientSecret &&
                  !paymentIntentLoading &&
                  !paymentIntentError &&
                  !paymentComplete &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <Stack spacing={1.5} alignItems="flex-start">
                      <Typography color="text.secondary">
                        Payment form unavailable. Please reach out to billing@leadgurus.com for assistance.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setPaymentIntentError('');
                          setPaymentError('');
                          setPaymentSuccess('');
                          setPaymentSubmissionStatus('');
                          setPaymentIntentRefreshNonce((prev) => prev + 1);
                        }}
                      >
                        Retry payment form
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            <Box display="flex" justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAchDialogOpen(true)}
                sx={{ borderRadius: 9999, textTransform: 'none' }}
              >
                Manual ACH details
              </Button>
            </Box>

            <Dialog open={achDialogOpen} onClose={() => setAchDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Manual ACH details</DialogTitle>
              <DialogContent dividers>
                {achLines.length ? (
                  <Stack spacing={1.5}>
                    {achLines.map((line) => (
                      <Typography key={line} fontFamily="'Roboto Mono', monospace" fontSize={13}>
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    ACH instructions will be provided upon request.
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAchDialogOpen(false)} sx={{ textTransform: 'none' }}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardHeader title="Invoice items" sx={{ pb: 0 }} />
              <CardContent>
                {invoice.line_items?.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.line_items.map((item) => (
                        <TableRow key={`${item.description}-${item.line_total}`}>
                          <TableCell>
                            <Typography fontWeight={600}>{item.description}</Typography>
                            {item.fee_percentage ? (
                              <Typography variant="caption" color="text.secondary">
                                Fee {item.fee_percentage}%
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Total due
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary">No line items available.</Typography>
                )}
              </CardContent>
            </Card>

            {invoice.territory_breakdown?.length ? (
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardHeader
                  title="Territory & Vertical Breakdown"
                  sx={{ pb: 0 }}
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadTerritoryCsv}
                      sx={{ borderRadius: 9999, textTransform: 'none' }}
                    >
                      Download CSV
                    </Button>
                  }
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Territory</TableCell>
                        <TableCell>Vertical</TableCell>
                        <TableCell align="right">Spend</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.territory_breakdown.map((row) => (
                        <TableRow key={`${row.territory}-${row.territory_id || 'na'}`}>
                          <TableCell>{row.territory}</TableCell>
                          <TableCell>{row.vertical || 'Unassigned'}</TableCell>
                          <TableCell align="right">{formatCurrency(row.spend)}</TableCell>
                        </TableRow>
                      ))}
                      {invoice.territory_breakdown_total ? (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }} colSpan={2}>
                            Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(invoice.territory_breakdown_total)}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : null}

            {clientBreakdown.length ? (
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardHeader
                  title="Client Breakdown"
                  sx={{ pb: 0 }}
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadClientBreakdownCsv}
                      sx={{ borderRadius: 9999, textTransform: 'none' }}
                    >
                      Download CSV
                    </Button>
                  }
                />
                <CardContent>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Fee</TableCell>
                        <TableCell align="right">Line total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientBreakdown.map((row, index) => (
                        <TableRow key={`${row.client_id || index}-${row.client_name || 'client'}`}>
                          <TableCell>{row.client_name || '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(row.spend_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.fee_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }} colSpan={3}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : null}

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardHeader title="Attachments" sx={{ pb: 0 }} />
              <CardContent>
                {attachments.length ? (
                  <Stack spacing={2}>
                    {attachments.map((attachment) => {
                      const key = attachment.id || attachment.download_url || attachment.filename;
                      const uploadedDate = formatDate(attachment.uploaded_at);
                      const uploadLabel = uploadedDate !== '—' ? `Uploaded ${uploadedDate}` : null;
                      const detailParts = [attachment.notes, uploadLabel].filter(Boolean);
                      return (
                        <Box key={key}>
                          <Typography
                            component="a"
                            href={attachment.download_url || attachment.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: 600, display: 'inline-block' }}
                          >
                            {attachment.filename || attachment.original_filename || 'Attachment'}
                          </Typography>
                          {detailParts.length ? (
                            <Typography variant="body2" color="text.secondary">
                              {detailParts.join(' • ')}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No attachments included.</Typography>
                )}
              </CardContent>
            </Card>

            <Divider />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Need help? Email <a href="mailto:billing@leadgurus.com">billing@leadgurus.com</a> and include
              your invoice number {invoice.number}.
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Container>
  );
}
